import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CdkDragDrop, transferArrayItem, copyArrayItem } from '@angular/cdk/drag-drop';
import { 
  SituViewWebApiClient, CreateAccessoryBundleRequest, CreateAccessoryBundleResponse,
  CreateAccessoryBundleItem, ListIfcFilesByHomeIdRequest, ListIfcFilesByHomeIdResponse,
  HomeIfcFile
} from 'situview-webapi-client';
import { AuthService, BreadcrumbService, StateService } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FormControl } from '@angular/forms';

interface BundleItem {
  homeIfcFileId: string;
  code: string;
  sortOrder: number;
  homeName?: string;
  homeIfcFileName?: string;
}

interface ExtendedHomeIfcFile extends HomeIfcFile {
  homeName?: string;
  code?: string;
}

@Component({
  selector: 'accessory-bundle-add',
  templateUrl: './accessory-bundle-add.component.html',
  styleUrls: ['./accessory-bundle-add.component.scss']
})
export class AccessoryBundleAddComponent extends SituBaseComponent implements OnInit {
  name: string = '';
  buildingInstructions: string = '';
  selectedItems: BundleItem[] = [];
  availableIfcFiles: ExtendedHomeIfcFile[] = [];
  filteredIfcFiles: ExtendedHomeIfcFile[] = [];
  
  searchControl = new FormControl();
  isModified: boolean = false;

  private _loading = false;
  public get loading(): boolean { return this._loading; }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private router: Router,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private situViewApiClient: SituViewWebApiClient
  ) {
    super(authService, stateService);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.breadcrumbService.setParam('accessoryBundleName', 'New Bundle');
    
    this._subscriptions.push(
      this.searchControl.valueChanges
        .pipe(
          debounceTime(300),
          distinctUntilChanged()
        )
        .subscribe(value => {
          this.filterIfcFiles(value);
        })
    );
  }

  protected onSelectedCompanyChange(): void {
    this.loadIfcFiles();
  }

  drop(event: CdkDragDrop<any[]>): void {
    if (event.container.id === 'selectedContainer' && event.previousContainer.id === 'selectedContainer') {
      // Reordering within selected items
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      this.updateSortOrder();
      this.checkIfModified();
    } else if (event.container.id === 'selectedContainer') {
      // Adding new item from available list
      const ifcFile: ExtendedHomeIfcFile = event.previousContainer.data[event.previousIndex];
      const newItem = this.convertToBundleItem(ifcFile);
      
      copyArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      
      event.container.data[event.currentIndex] = newItem;
      this.updateSortOrder();
      this.checkIfModified();
    }
  }

  onRemoveItem(index: number): void {
    this.selectedItems.splice(index, 1);
    this.updateSortOrder();
    this.checkIfModified();
  }

  isItemSelected(ifcFileId: string): boolean {
    return this.selectedItems.some(item => item.homeIfcFileId === ifcFileId);
  }

  canSave(): boolean {
    return !!this.name && this.selectedItems.length > 0;
  }

  save(): void {
    if (!this.canSave()) return;

    this.loading = true;
    
    const request = new CreateAccessoryBundleRequest();
    request.companyId = this.selectedCompany?.id;
    request.name = this.name;
    request.buildingInstructions = this.buildingInstructions;
    request.items = this.selectedItems.map(item => {
      const bundleItem = new CreateAccessoryBundleItem();
      bundleItem.homeIfcFileId = item.homeIfcFileId;
      bundleItem.code = item.code;
      bundleItem.sortOrder = item.sortOrder;
      return bundleItem;
    });

    this.situViewApiClient.createAccessoryBundle(request)
      .then((response: CreateAccessoryBundleResponse) => {
        if (response.success && response.accessoryBundle) {
          this.snackBar.open('Accessory bundle created successfully!', 'OK', { duration: 2000 });
          this.router.navigate(['/admin/accessory-bundle', response.accessoryBundle.id]);
        } else {
          this.snackBar.open('Error creating accessory bundle!', 'OK', { duration: 2000 });
        }
        this.loading = false;
      })
      .catch((err: any) => {
        this.loading = false;
        this.snackBar.open('Error creating accessory bundle!', 'OK', { duration: 2000 });
      });
  }

  cancel(): void {
    this.router.navigate(['/admin/accessory-bundle']);
  }

  private loadIfcFiles(): void {
    if (!this.selectedCompany?.id) return;

    this.loading = true;
    this.availableIfcFiles = [];
    
    const request = new ListIfcFilesByHomeIdRequest();
    request.companyId = this.selectedCompany.id;
    // Don't set homeId - we want all IFC files for the company
    request.isMaster = false; // We only want option files
    
    this.situViewApiClient.listIfcFilesByHomeId(request)
      .then((response: ListIfcFilesByHomeIdResponse) => {
        if (response.success && response.ifcFiles) {
          // Process IFC files
          this.availableIfcFiles = response.ifcFiles.map((file: HomeIfcFile) => {
            // Extract code and home from callerReferenceKey - format: builder\home\code
            const parts = file.callerReferenceKey?.split('\\') || [];
            const homeName = parts.length > 1 ? parts[1] : '';
            const code = parts.length > 2 ? parts[2] : parts[parts.length - 1] || '';
            
            return {
              ...file,
              homeName: homeName,
              code: code
            } as ExtendedHomeIfcFile;
          });
          
          // Filter to only show files marked as accessories
          this.availableIfcFiles = this.availableIfcFiles.filter(f => f.isAccessory === true);
          
          this.filteredIfcFiles = [...this.availableIfcFiles];
        } else {
          this.availableIfcFiles = [];
          this.filteredIfcFiles = [];
        }
        this.loading = false;
      })
      .catch((error) => {
        this.loading = false;
        this.snackBar.open('Error loading IFC files!', 'OK', { duration: 2000 });
        this.availableIfcFiles = [];
        this.filteredIfcFiles = [];
      });
  }

  private filterIfcFiles(searchTerm: string): void {
    if (!searchTerm) {
      this.filteredIfcFiles = [...this.availableIfcFiles];
    } else {
      const term = searchTerm.toLowerCase();
      this.filteredIfcFiles = this.availableIfcFiles.filter(file => 
        file.name?.toLowerCase().includes(term) ||
        file.code?.toLowerCase().includes(term)
      );
    }
  }

  private convertToBundleItem(ifcFile: ExtendedHomeIfcFile): BundleItem {
    return {
      homeIfcFileId: ifcFile.id!,
      code: ifcFile.code || '',
      sortOrder: this.selectedItems.length,
      homeName: ifcFile.homeName,
      homeIfcFileName: ifcFile.name
    };
  }

  private updateSortOrder(): void {
    this.selectedItems.forEach((item, index) => {
      item.sortOrder = index;
    });
  }

  checkIfModified(): void {
    this.isModified = this.selectedItems.length > 0 || !!this.name || !!this.buildingInstructions;
  }
}